import { type FC, useMemo, useState } from 'react';
import {
  PersonalDataLabel,
  PropertyLabel,
} from 'constants/objectManager.constants';
import { getText } from 'i18n/intl';
import { castArray } from 'lodash';
import { fieldMappingSelector } from 'slices/field.slice';
import { organizationMapSelector } from 'slices/organizationHierarchy.slice';
import { useAppSelector } from 'slices/store';
import type {
  BatchEditOperation,
  TAvailabilityRelation,
} from 'types/batchEditObject.type';
import {
  isBulkEditJob,
  isDeleteJob,
  isExportJob,
} from 'types/objectJob.guards';
import type {
  TObjectBulkEditJob,
  TObjectDeleteJob,
  TObjectExportJob,
  TObjectImportJob,
} from 'types/objectJob.type';
import type { TField, TOrganizationNode } from '@timeedit/types';
import { Typography } from '@timeedit/ui-components';
import { Button } from '@timeedit/ui-components/antd';
import useExportAttributes from '../../../../hooks/useExportAttributes';
import DeleteJobObjectsModal from './DeleteJobObjectsModal';
import JobFieldsModal from './JobFieldsModal';
import JobObjectsModal from './JobObjectsModal';
import './OperationInfo.scss';

const { Title, Text } = Typography;

type Props = {
  hideShowAllObjects?: boolean;
  job:
    | TObjectBulkEditJob
    | TObjectExportJob
    | TObjectDeleteJob
    | TObjectImportJob;
};

const LIMIT_OBJ_IDS_TO_SHOW = 7;
const LIMIT_FIELD_IDS_TO_SHOW = LIMIT_OBJ_IDS_TO_SHOW;

const MULTI_OPERATION_TYPE_STRINGS = {
  replace: 'Replace',
  add: 'Add',
};

const OperationInfo: FC<Props> = (props) => {
  const hideShowAllObjects = props.hideShowAllObjects ?? false;
  const fields = useAppSelector(fieldMappingSelector);
  const organizations = useAppSelector(organizationMapSelector);

  const definedFields = useMemo<Record<number, TField>>(() => {
    return Object.entries(fields).reduce(
      (acc, [key, value]) => {
        if (value !== undefined) {
          acc[Number(key)] = value;
        }
        return acc;
      },
      {} as Record<number, TField>
    );
  }, [fields]);

  const extIdsToProcess = useMemo(() => {
    if (props.job.operationType === 'IMPORT') return props.job.objectsToProcess;

    return props.job.objectsToProcess.map((o) => o.extId);
  }, [props.job.objectsToProcess, props.job.operationType]);

  const objectList = useMemo(() => {
    const result = extIdsToProcess.slice(0, LIMIT_OBJ_IDS_TO_SHOW).join(', ');

    if (extIdsToProcess.length > LIMIT_OBJ_IDS_TO_SHOW) {
      return result + '...';
    }
    return result;
  }, [extIdsToProcess]);

  const [modalObjectsOpen, setModalObjectsOpen] = useState(false);
  const [modalFieldsOpen, setModalFieldsOpen] = useState(false);

  const operationComponents = useMemo(() => {
    if (!isBulkEditJob(props.job)) return [];
    return props.job.operations.map((op, i) => (
      <Operation
        key={i}
        operation={op}
        fields={definedFields}
        organizations={organizations}
      />
    ));
  }, [props.job, definedFields, organizations]);

  const fieldComponent = useMemo(() => {
    if (!isExportJob(props.job) || props.job.fields.length === 0) return null;
    const exportFields = props.job.fields
      .map((fieldId) => definedFields[fieldId])
      .filter((field): field is TField => field !== undefined);
    return renderFieldNames(exportFields);
  }, [props.job, definedFields]);

  const attributesComponent = useMemo(() => {
    if (!isExportJob(props.job)) return null;
    return <Attributes attributes={props.job.attributes} />;
  }, [props.job]);

  return (
    <>
      <Title level={4}>Objects</Title>
      <div className="job-detail-operation-box">
        <table>
          <tbody>
            <tr>
              <td>
                <Text>Object type:</Text>
              </td>
              <td>
                <Text strong>{props.job.objectTypeName}</Text>
              </td>
            </tr>
            {'multiOperationType' in props.job && (
              <tr>
                <td>
                  <Text>Operation type:</Text>
                </td>
                <td>
                  <Text strong>
                    {MULTI_OPERATION_TYPE_STRINGS[props.job.multiOperationType]}
                  </Text>
                </td>
              </tr>
            )}
            <tr>
              <td>
                <Text>Objects:</Text>
              </td>
              {props.job.status != 'QUEUED' ||
              props.job.objectsToProcess.length > 0 ? (
                <td>
                  <Text strong>{props.job.objectsToProcess.length}</Text>{' '}
                  {objectList}{' '}
                  {!hideShowAllObjects && (
                    <Button
                      size="small"
                      onClick={() => setModalObjectsOpen(true)}
                    >
                      Show all
                    </Button>
                  )}
                </td>
              ) : (
                <td>
                  <Text strong>Pending</Text>
                </td>
              )}
            </tr>
            {operationComponents}
            {fieldComponent != null && (
              <tr>
                <td>Fields</td>
                <td>
                  {fieldComponent}{' '}
                  <Button size="small" onClick={() => setModalFieldsOpen(true)}>
                    Show all
                  </Button>
                </td>
              </tr>
            )}
            {attributesComponent}
          </tbody>
        </table>
      </div>
      {isDeleteJob(props.job) ? (
        <DeleteJobObjectsModal
          open={modalObjectsOpen}
          job={props.job}
          onClose={() => setModalObjectsOpen(false)}
        />
      ) : (
        <JobObjectsModal
          open={modalObjectsOpen}
          job={props.job}
          onClose={() => setModalObjectsOpen(false)}
          objectExtIds={extIdsToProcess}
          objectTypeId={props.job.objectTypeId}
          objectTypeName={props.job.objectTypeName}
        />
      )}
      {isExportJob(props.job) && modalFieldsOpen && (
        <JobFieldsModal
          open={modalFieldsOpen}
          job={props.job}
          onClose={() => setModalFieldsOpen(false)}
          fields={definedFields}
          fieldIds={props.job.fields ?? []}
          objectTypeName={props.job.objectTypeName}
        />
      )}
    </>
  );
};

const getAttributeName = (
  operation: BatchEditOperation,
  fields: Record<number, TField>
): string => {
  switch (operation.category) {
    case 'field':
      return fields[operation.fieldId]?.name ?? `field_${operation.fieldId}`;
    case 'generalInfo':
      return (
        GENERAL_INFORMATION_STRINGS[operation.attribute] ?? operation.attribute
      );
    case 'organizations':
      return 'Organizations';
    case 'membersAndRelations':
      return (
        MEMBERSHIP_AND_RELATIONS_LABELS[operation.attribute] ??
        operation.attribute
      );
    default:
      return 'unknown';
  }
};

const formatValue = (
  values: string | string[] | number[] | TAvailabilityRelation[],
  operation: BatchEditOperation,
  organizations: Record<string, TOrganizationNode>
): string => {
  if (operation.category === 'organizations') {
    return castArray(values as string | string[])
      .map((id) => organizations[id]?.name ?? id)
      .join(', ');
  }
  if (
    operation.category === 'generalInfo' &&
    operation.attribute === 'property'
  ) {
    return castArray(values as string | string[])
      .map((v) => PropertyLabel[v as keyof typeof PropertyLabel] ?? v)
      .join(', ');
  }
  if (
    operation.category === 'generalInfo' &&
    operation.attribute === 'personalInformation'
  ) {
    return castArray(values as string | string[])
      .map((v) => PersonalDataLabel[v as keyof typeof PersonalDataLabel] ?? v)
      .join(', ');
  }
  if (
    operation.category === 'membersAndRelations' &&
    operation.attribute === 'availabilityRelations'
  ) {
    return (values as TAvailabilityRelation[])
      .map(
        (v) =>
          `[availabilityTypeId: ${v.availabilityTypeId}, objectId: ${v.objectId}]`
      )
      .join(', ');
  }
  return castArray(values as string | string[])
    .map((v) => String(v))
    .join(', ');
};

const OperationDetail: FC<{ label: string; value?: string }> = ({
  label,
  value,
}) => (
  <span className="operation-detail">
    <Text strong>
      {label}
      {value ? ':' : ''}
    </Text>
    {value ? ` ${value}` : ''}
  </span>
);

const renderFieldNames = (fields: TField[]): string => {
  const fieldNames = fields
    .slice(0, LIMIT_FIELD_IDS_TO_SHOW)
    .map((field) => field?.name)
    .join(', ');
  if (fields.length > LIMIT_FIELD_IDS_TO_SHOW) {
    return fieldNames + '...';
  }
  return fieldNames;
};

const Attributes: FC<{ attributes: string[] }> = ({ attributes }) => {
  const { attributesMap } = useExportAttributes({
    showMembers: true,
    showAvailabilityRelated: true,
    showOptionalRelated: true,
    showRelations: true,
  });
  return (
    <tr>
      <td>Attributes</td>
      <td>
        {attributes
          .map((attr) => attributesMap?.[attr]?.label ?? attr)
          .join(', ')}
      </td>
    </tr>
  );
};

const Operation: FC<{
  operation: BatchEditOperation;
  fields: Record<number, TField>;
  organizations: Record<string, TOrganizationNode>;
}> = ({ operation, fields, organizations }) => {
  const attributeName = getAttributeName(operation, fields);
  const isFindReplace = 'value' in operation && 'searchValue' in operation;

  return (
    <tr>
      <td>{attributeName}</td>
      <td>
        {isFindReplace ? (
          <>
            <OperationDetail
              label={OPERATION_STRINGS.find}
              value={formatValue(
                operation.searchValue,
                operation,
                organizations
              )}
            />
            {'rule' in operation && (
              <OperationDetail
                label={getText(
                  'object_manager.bulk-edit-modal.match-rule.label'
                )}
                value={
                  operation.rule === 'matchAll'
                    ? getText(
                        'object_manager.bulk-edit-modal.match-rule.match-all'
                      )
                    : getText(
                        'object_manager.bulk-edit-modal.match-rule.match-any'
                      )
                }
              />
            )}
            <OperationDetail
              label={OPERATION_STRINGS.replaceWith}
              value={formatValue(operation.value, operation, organizations)}
            />
          </>
        ) : (
          <OperationDetail
            label={
              OPERATION_STRINGS[operation.operation] ?? operation.operation
            }
            value={
              'value' in operation
                ? formatValue(operation.value, operation, organizations)
                : 'searchValue' in operation
                  ? formatValue(operation.searchValue, operation, organizations)
                  : undefined
            }
          />
        )}
      </td>
    </tr>
  );
};

const GENERAL_INFORMATION_STRINGS = {
  personalInformation: getText(
    'object_manager.bulk-edit-modal.general.personal-information.label'
  ),
  isActive: getText('object_manager.bulk-edit-modal.general.is-active.label'),
  property: getText('object_manager.bulk-edit-modal.general.property.label'),
} as const;

const OPERATION_STRINGS = {
  add: getText('object_manager.bulk-edit-modal.add.label'),
  removeSpecific: getText(
    'object_manager.bulk-edit-modal.removeSpecific.label'
  ),
  find: getText('object_manager.bulk-edit-modal.findReplace.find-input-label'),
  replaceWith: getText(
    'object_manager.bulk-edit-modal.findReplace.replace-input-label'
  ),
  removeAll: getText('object_manager.bulk-edit-modal.removeAll.label'),
  replaceAll: getText('object_manager.bulk-edit-modal.replaceAll.label'),
  findReplace: getText('object_manager.bulk-edit-modal.findReplace.label'),
  checkAll: getText('object_manager.bulk-edit-modal.checkAll.label'),
  uncheckAll: getText('object_manager.bulk-edit-modal.uncheckAll.label'),
};

const MEMBERSHIP_AND_RELATIONS_LABELS = {
  members: getText(
    'object_manager.object_table.drawer.associations.memberships.title'
  ),
  relations: getText(
    'object_manager.object_table.drawer.associations.relations.title'
  ),
  availabilityRelations: getText(
    'object_manager.object_table.drawer.associations.availability_related.title'
  ),
  optionalRelations: getText(
    'object_manager.object_table.drawer.associations.optional_related.title'
  ),
};

export default OperationInfo;
